<template>
  <div class="form-group">
    <div class="select-wrapper">
      <label class="form-label"
             :data-content="label"
             :class="formLabelClass"
             v-if="showlabel">
        <span class="">{{ label }}</span>
        <span v-if="required"> *</span>
        <span v-if="infoHint">
          <a :title="infoHint"
             class="info ml-3"
             v-b-tooltip.hover>
            <InfoSvg class="info-svg"/>
          </a>
        </span>
      </label>
      <div v-if="editMode"
           class="select-wrap"
           :class="{ 'select-auto-size': adjustWidthToSelectedOption }">
        <span v-if="adjustWidthToSelectedOption"
              class="select-auto-size-label"
              :class="['form-control', inputClass, {
                'gray-input': variant === 'gray',
                'not-selected': !value,
                'text-like': textLikeInput
              }]"
              aria-hidden="true">
          {{ selectedOptionLabel }}
        </span>
        <select class="form-control"
                :disabled="readonly"
                :class="[inputClass, {
                  'gray-input': variant === 'gray',
                  'not-selected': !value,
                  'text-like': textLikeInput,
                  'select-auto-size-control': adjustWidthToSelectedOption
                }]"
                @input="handleInput"
                :value="value">
          <option v-if="nullOption" value="">{{ nullOptionTitle }}</option>
          <option v-for="o in options"
                  :value="valuekey ? o[valuekey] : o.id"
                  :key="'opt-'+o.id+'-'+label">
            {{ title ? t(o,title) : (translate && $te(o.title) ? $t(o.title) : o.title) }}
          </option>
        </select>

        <slot/>
        <ValidationError :class="{'mt-1': !validationSpace, 'mt-3': validationSpace }"
                         v-if="showErrors"
                         :validationId="validationId"/>
      </div>
      <div v-if="!editMode" class="word-break">{{ stringValue }}</div>
    </div>
  </div>
</template>

<script setup>
import ValidationError from "@/components/form/ValidationError"
import InfoSvg from '@/assets/svg-vue/general/question.svg'
import {computed, getCurrentInstance, onBeforeMount} from "vue"

const props = defineProps({
  label: {
    type: String
  },
  showErrors: {
    type: Boolean,
    default: false
  },
  validationId: {},
  value: {},
  valuekey: {},
  options: {
    type: Array,
    required: true
  },
  showlabel: {
    type: Boolean,
    default: false
  },
  translate: {
    type: Boolean,
    default: true
  },
  title: {
    type: String
  },
  variant: {
    type: String,
    default: 'gray'
  },
  nullOption: {
    type: Boolean,
    default: true
  },
  nullOptionTitle: {
    type: String,
    default: '-'
  },
  required: {
    type: Boolean,
    default: false
  },
  infoHint: {
    type: String,
    default: ''
  },
  formLabelClass: {
    type: String,
  },
  inputClass: {
    type: String,
  },
  readonly: {},
  editMode: {
    type: Boolean,
    default: true
  },
  textLikeInput: {
    type: Boolean,
    default: false
  },
  validationSpace: {
    type: Boolean,
    default: false
  },
  adjustWidthToSelectedOption: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['input'])

let i18n = null
let tFn = null

const stringValue = computed(() => {
  if(!props.value) return '-'
  const obj = (props.options || []).find(o => o.id === props.value)
  const title = obj?.[(props.title || 'title')] ?? null
  if(props.translate && title) return i18n.t(title)
  return title || '-'
})

const selectedOptionLabel = computed(() => {
  if(!props.value) return props.nullOption ? props.nullOptionTitle : ''
  const obj = (props.options || []).find(o => (props.valuekey ? o[props.valuekey] : o.id) === props.value)
  if(!obj) return ''
  if(props.title) return tFn ? tFn(obj, props.title) : ''
  return props.translate && i18n?.te(obj.title) ? i18n.t(obj.title) : obj.title
})

const handleInput = (e) => {
  emit('input', e.target.value)
}

onBeforeMount(() => {
  const ctx = (getCurrentInstance())?.ctx
  i18n = ctx?.$i18n
  tFn = ctx?.t
})
</script>

<style scoped>
.select-wrap.select-auto-size {
  position: relative;
  display: inline-block;
}
.select-auto-size-label {
  display: inline-block;
  white-space: nowrap;
  pointer-events: none;
}
.select-auto-size-control {
  position: absolute !important;
  inset: 0;
  width: 100% !important;
  height: 100%;
  margin: 0;
  color: transparent !important;
  background-color: transparent !important;
  border-color: transparent !important;
  cursor: pointer !important;
}
.select-auto-size-control option {
  color: initial;
  background-color: initial;
}
</style>
